import React, { useEffect, useMemo, useState } from "react";
import { LiveChatUser } from "@scrile/api-provider/dist/api/LivechatsProvider";
import { getProduceStreams } from "lib/liveStreamHelpers";
import { MediaConstraints, MediaDevicePermissions, ProduceStream, ProduceUser, UserListItemIcon } from "types";
import BaseButton from "components/BaseButton";
import BaseIcon from "components/BaseIcon";
import ChatTimer from "components/ChatTimer";
import DotFlashingAnimation from "components/DotFlashingAnimation";
import StreamView from "components/StreamView";
import ConferenceShareModal from "../ConferenceShareModal";

interface Props {
  threadId: string | undefined;
  conferenceUuid: string;
  loading: boolean;
  userList: LiveChatUser[];
  token: string;
  showConfirmationExit: boolean;
  audioInputs: MediaDeviceInfo[];
  videoInputs: MediaDeviceInfo[];
  users: ProduceUser[];
  me: ProduceUser | null;
  isConferenceOwner: boolean;
  muteUser: (user: LiveChatUser) => void;
  managedConstraints: MediaConstraints | null;
  permissions?: MediaDevicePermissions;
  streamStartedDate: string;
  onChangeConstraints: (constraints: MediaConstraints) => void;
  onCloseConference: () => void;
  onCloseStream: () => void | Promise<void>;
}

export default function ConferenceView({
  users,
  me,
  muteUser,
  isConferenceOwner,
  conferenceUuid,
  streamStartedDate,
  onCloseStream,
  onCloseConference,
  threadId,
  loading,
  userList,
  token,
  showConfirmationExit,
  audioInputs,
  videoInputs,
  managedConstraints,
  permissions,
  onChangeConstraints,
}: Props) {
  const [showShare, setShowShare] = useState(false);
  const [streams, setStreams] = useState<ProduceStream[]>([]);

  const userListItemIcon = useMemo<UserListItemIcon>(() => {
    return {
      onIconCLick: (user: LiveChatUser) => muteUser(user),
      showIcon: isConferenceOwner,
      showIconOnMe: false,
      meId: me?.user.id,
    };
  }, [me, muteUser, isConferenceOwner]);

  const onClose = async () => {
    await onCloseStream();
    onCloseConference();
  };

  useEffect(() => {
    const result: ProduceStream[] = [];
    if (me) {
      result.push(...getProduceStreams(me));
    }
    users.forEach((user) => result.push(...getProduceStreams(user)));
    setStreams(result);
  }, [me, users]);

  const slotVideoScreen = (() => {
    if (!users.length) {
      return <DotFlashingAnimation className="conference-view__dot-animation" />;
    }
  })();

  const slotHeaderButtons = (
    <BaseButton className="stream-view__header-btn __share" name={"share"} onClick={() => setShowShare(true)}>
      <BaseIcon className="stream-view__header-icon">share-outline</BaseIcon>
    </BaseButton>
  );

  const slotTimer = (
    <ChatTimer showTimeIfNegative streamStartedDate={streamStartedDate}>
      {"Live chat"}
    </ChatTimer>
  );

  return (
    <>
      <ConferenceShareModal show={showShare} conferenceUuid={conferenceUuid} onClose={() => setShowShare(false)} />

      <StreamView
        threadId={threadId}
        token={token}
        loading={loading}
        userList={userList}
        showConfirmationExit={showConfirmationExit}
        audioInputs={audioInputs}
        videoInputs={videoInputs}
        managedConstraints={managedConstraints}
        permissions={permissions}
        onChangeConstraints={onChangeConstraints}
        onClose={onClose}
        className="conference-view"
        produce
        produceUser={null}
        enableUserSearch
        allowGuestMessage
        me={me}
        userListItemIcon={userListItemIcon}
        slotHeaderButtons={slotHeaderButtons}
        includeGuestsList
        slotTimer={slotTimer}
        slotVideoScreen={slotVideoScreen}
        streams={streams}
        hideVolumeControl
        hideChatOnStart
      />
    </>
  );
}
